import React, {
  FC,
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { List, Map } from "immutable";
import { useTranslation } from "react-i18next";
import { OutgoingRowLayoutTier } from "types/outgoing";
import Button from "../../../../components/Button/Button";
import useContainerQuery from "../../../../hooks/useContainerQuery";
import {
  HeaderWrapper,
  ParcelsWrapper,
} from "../../../../styles/parcelsStyles";
import { getOutgoingLayoutTier } from "../rowLayout";
import {
  ButtonWrapper,
  ClickableDiv,
  CollapseWrapper,
} from "./OutgoingSection.styles";

interface OutgoingSectionProps {
  fetchData: (data: any) => void;
  itemRenderer: (item: any, layoutTier: OutgoingRowLayoutTier) => ReactNode;
  isCollapseOpen?: boolean;
  children: ReactNode;
  data: any;
  countItemsPerSection?: number;
}

interface SectionItemsProps {
  items: any;
  itemRenderer: (item: any, layoutTier: OutgoingRowLayoutTier) => ReactNode;
}

// Owns the width measurement (like StorageList does) so the ref is attached
// on this component's first render — OutgoingSection itself renders null
// while its bucket is empty, and useContainerQuery only observes the node
// present on mount.
const SectionItems: FC<SectionItemsProps> = ({ items, itemRenderer }) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  // The width available to the rows changes with both the viewport and the
  // right-panel squeeze, so measure the list itself instead of the screen.
  const layoutTier = useContainerQuery<OutgoingRowLayoutTier>(
    wrapperRef,
    getOutgoingLayoutTier,
    "full",
  );

  return (
    <ParcelsWrapper ref={wrapperRef}>
      {items.map((item: any) => itemRenderer(item, layoutTier))}
    </ParcelsWrapper>
  );
};

const OutgoingSection: FC<OutgoingSectionProps> = ({
  data,
  isCollapseOpen = false,
  fetchData,
  children,
  itemRenderer,
  countItemsPerSection = 10,
}) => {
  const { t } = useTranslation("common");
  const [isOpened, setCollapseOpened] = useState(isCollapseOpen);
  const items = data.get("items", List());
  const type = data.get("type");
  const currentPage = data.get("page") || 1;
  const total = parseInt(data.get("total", items.size), 10);
  const isLoading = data.get("isLoading", false);
  const loadData = (page: number) =>
    Map({
      type,
      params: {
        page: page,
        per_page: countItemsPerSection,
      },
    });

  const sortedItems = useMemo(
    () =>
      items.sort(
        (a: any, b: any) =>
          +new Date(b.get("created_at")) - +new Date(a.get("created_at")),
      ),
    [items],
  );

  useEffect(() => {
    fetchData(loadData(1));
  }, []);

  const handleLoadMore = useCallback(() => {
    fetchData(loadData(currentPage + 1));
  }, [fetchData, loadData, currentPage]);
  const toggleCollapse = () => setCollapseOpened(!isOpened);

  const headerChildern = () => {
    return <>{children}</>;
  };

  if (!items.size) return null;

  return (
    <section>
      <HeaderWrapper>
        <ClickableDiv onClick={toggleCollapse}>{headerChildern()}</ClickableDiv>
      </HeaderWrapper>
      <CollapseWrapper isOpened={!!isOpened}>
        <SectionItems items={sortedItems} itemRenderer={itemRenderer} />
        <ButtonWrapper>
          <Button
            disabled={isLoading}
            isLoading={isLoading}
            hidden={sortedItems.size === total}
            onClick={handleLoadMore}
          >
            {t("parcels.loadMore")}
          </Button>
        </ButtonWrapper>
      </CollapseWrapper>
    </section>
  );
};

export default memo(OutgoingSection);
